import { rawString, type LanguageCore } from './Language';

/**
 * A context-keyed translation entry, as front-loaded from MX
 * (RetailerSettings.contextTranslations — see PDP-1643 Slice 1). The UI
 * resolves these locally instead of firing a per-entity MX `resolve` call.
 *
 * A `null` context value is a wildcard: it matches any request value for that
 * dimension (mirrors MX's wildcard fallback).
 */
export interface ContextTranslation {
  key: string;
  value: string;
  context: Record<string, string | null>;
}

type RequestContext = Record<string, string>;

const isContextObject = (
  context: unknown,
): context is Record<string, string | null> =>
  !!context && typeof context === 'object' && !Array.isArray(context);

/** An entry matches when every dimension it pins is satisfied by the request. */
const matchesRequest = (
  entryContext: Record<string, string | null>,
  request: RequestContext,
): boolean =>
  Object.entries(entryContext).every(
    ([dim, value]) => value === null || request[dim] === value,
  );

/** Concrete (non-wildcard) dimensions — more concrete is more specific. */
const specificity = (entryContext: Record<string, string | null>): number =>
  Object.values(entryContext).filter((value) => value !== null).length;

/** Deterministic signature of a context, for tie-breaking equal-specificity matches. */
const contextSignature = (
  entryContext: Record<string, string | null>,
): string =>
  JSON.stringify(
    Object.entries(entryContext).sort(([a], [b]) => a.localeCompare(b)),
  );

/**
 * Resolve a context-keyed translation: among the entries for `langKey` whose
 * context is satisfied by `request`, return the most specific match's value
 * (mirrors MX `resolve`'s cascade — specific context wins, wildcard falls back).
 *
 * Key matching is case-insensitive (like `rawString`). Ties on specificity are
 * broken deterministically by context signature, so the result never depends on
 * the order MX returned rows in. Malformed rows (missing/non-object context)
 * are skipped rather than throwing.
 */
export const resolveContextTranslation = (
  table: ContextTranslation[],
  langKey: string,
  request: RequestContext,
): string | undefined => {
  const wanted = langKey.toLowerCase();
  let best: ContextTranslation | undefined;
  let bestScore = -1;
  let bestSignature = '';

  for (const entry of table) {
    if (!isContextObject(entry?.context)) continue;
    if (entry.key.toLowerCase() !== wanted) continue;
    if (!matchesRequest(entry.context, request)) continue;

    const score = specificity(entry.context);
    const signature = contextSignature(entry.context);
    if (
      best === undefined ||
      score > bestScore ||
      (score === bestScore && signature < bestSignature)
    ) {
      best = entry;
      bestScore = score;
      bestSignature = signature;
    }
  }

  return best?.value;
};

// Global store, hydrated from RetailerSettings.contextTranslations (parallels
// the flat-translation global store in Language.tsx). Indexed by lowercased key
// so lookups don't rescan entries for unrelated keys.
let contextByKey = new Map<string, ContextTranslation[]>();

export const setContextTranslations = (table: ContextTranslation[]) => {
  const byKey = new Map<string, ContextTranslation[]>();
  for (const entry of table) {
    if (typeof entry?.key !== 'string' || !isContextObject(entry.context)) {
      continue;
    }
    const key = entry.key.toLowerCase();
    const existing = byKey.get(key);
    if (existing) existing.push(entry);
    else byKey.set(key, [entry]);
  }
  contextByKey = byKey;
};

/**
 * `rawString` for a context-keyed translation: resolve `(langKey, context)`
 * against the front-loaded table, then run it through the same token
 * interpolation + sanitization as `rawString`. Falls back through `rawString`'s
 * own cascade — when there's no context match, the flat `langKey` translation,
 * then the `fallback` string.
 */
export const rawContextString = ({
  langKey,
  context,
  replacementTokens,
  fallback,
  purifyOptions,
}: {
  langKey: string;
  context: RequestContext;
} & Pick<LanguageCore, 'replacementTokens' | 'fallback' | 'purifyOptions'>) => {
  const entries = contextByKey.get(langKey.toLowerCase()) ?? [];
  const value = resolveContextTranslation(entries, langKey, context);
  return rawString({
    langKey,
    content: value,
    replacementTokens,
    fallback,
    purifyOptions,
  });
};

/**
 * Context-only variant of {@link rawContextString}: returns `undefined` when no
 * context entry matches, rather than falling back to the flat `langKey`
 * translation. Use this when the caller drives its own fallback chain (e.g. a
 * `??` cascade) and the flat-key fallback would shadow more specific tiers — a
 * flat `pdx.resolution.label` would otherwise win for every option and suppress
 * the per-code keys below it. A real match is still token-interpolated and
 * sanitized.
 */
export const resolveContextString = ({
  langKey,
  context,
  replacementTokens,
  purifyOptions,
}: {
  langKey: string;
  context: RequestContext;
} & Pick<LanguageCore, 'replacementTokens' | 'purifyOptions'>) => {
  const entries = contextByKey.get(langKey.toLowerCase()) ?? [];
  const value = resolveContextTranslation(entries, langKey, context);
  // An empty match is not a usable label and must not reach rawString — there
  // its falsy content would let the flat langKey win, the exact flat fallback
  // this helper exists to suppress. Return undefined so the caller cascade runs.
  if (!value) return undefined;
  return rawString({
    langKey,
    content: value,
    replacementTokens,
    purifyOptions,
  });
};
